import React, { useMemo, useState } from "react";
import { callable } from "../../../firebase.js";
import { useFirestoreCollection, useFirestoreDoc } from "../../../lib/useFirestore.js";
import { Crumbs, Back } from "./nav.jsx";

/**
 * Screen 6 (v1) — Send. The job is already chosen. Slice 4 makes it live: the
 * Send button calls the sendCaptureSheet callable (upsert capture rows by
 * captureId, copy the 6 picks per capture to job storage, add the PDF to the
 * job's files). Multi-day routes the rows to the chosen day's session. On
 * success → the Sent confirmation screen.
 */
const monthDay = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${MON[(m || 1) - 1]} ${d}`;
};

export default function Send({ nav, go }) {
  const { jobId, jobLabel } = nav;
  const { data: job } = useFirestoreDoc(jobId ? `jobs/${jobId}` : null);
  const { data: groups } = useFirestoreCollection(jobId ? `jobs/${jobId}/captureSheet` : null);

  const days = job?.shootDates || [];
  const multiDay = days.length > 1;
  const [day, setDay] = useState(0); // 0-based index; callable wants 1-based
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The included set for the chosen day (confirmed groups, captures with picks).
  const { totalCaps, photos } = useMemo(() => {
    const confirmed = groups.filter((g) => g.isConfirmed);
    const caps = confirmed.flatMap((g) => (g.captures || []).filter((c) => (c.selected || []).length > 0))
      .filter((c) => !multiDay || (c.day ?? 1) === day + 1);
    return { totalCaps: caps.length, photos: caps.reduce((n, c) => n + (c.selected || []).length, 0) };
  }, [groups, multiDay, day]);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await callable("sendCaptureSheet")({ jobId, day: multiDay ? day + 1 : undefined });
      go("sent", { sent: { ...res.data, dayLabel: multiDay ? `Day ${day + 1}` : null } });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Crumbs
        trail={[
          { label: "Sheets", screen: "home", patch: { jobId: null } },
          { label: jobLabel, screen: "job" },
          { label: "Capture Sheet", screen: "groups" },
          { label: "Send" },
        ]}
        go={go}
      />
      <Back label="Proof" onClick={() => go("proof")} />
      <div className="qhead">Send it<span className="d">capture list + contact sheet → this job</span></div>

      <div className="card">
        <div className="togglerow">
          <span style={{ fontSize: 15 }}>☑</span>
          <span><b>{totalCaps} capture rows</b> → the job's capture list
            <div className="td">Updates by capture — re-sending never duplicates, and edits made in Suite stay.</div>
          </span>
        </div>
        <div className="togglerow">
          <span style={{ fontSize: 15 }}>☑</span>
          <span><b>{photos} photos</b> (your picks per capture) → job storage</span>
        </div>
        <div className="togglerow">
          <span style={{ fontSize: 15 }}>☑</span>
          <span><b>Contact-sheet PDF</b> → job files
            <div className="td">The pages you just proofed.</div>
          </span>
        </div>
      </div>

      {multiDay && (
        <div className="card" style={{ fontSize: 12.5 }}>
          <b style={{ fontWeight: 500 }}>Which day?</b> <span style={{ color: "var(--s-muted)" }}>(multi-day job)</span>
          <div className="dayrow">
            {days.map((d, i) => (
              <button key={d} type="button" className={`daychip ${i === day ? "on" : ""}`} onClick={() => setDay(i)}>
                Day {i + 1} — {monthDay(d)} {i === day ? "✓" : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="proofnote" style={{ color: "#9A1B1B" }}>Couldn't send: {error}</div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" className="btnP" disabled={busy || totalCaps === 0} onClick={send}>
          {busy ? "Sending…" : "Send to job"}
        </button>
        <button type="button" className="btnS" onClick={() => go("proof")}>Back to proof</button>
      </div>
      <div style={{ fontSize: 11, color: "var(--s-muted)", marginTop: 8 }}>
        Sends the {multiDay ? `Day ${day + 1}` : "capture"} rows, photos and the contact sheet into this job. Build the proof first if the button is blocked.
      </div>
    </>
  );
}
